/**
 * Secure Storage Management Utility
 * Handles admin (sessionStorage) and customer (localStorage) tokens
 * with proper isolation and cleanup
 */

// ==================== ADMIN STORAGE (sessionStorage - Auto-clears on tab close) ====================

export const AdminStorage = {
  /**
   * Cache the non-sensitive admin/role display object after login.
   * SECURITY (2026-09-25 migration): the actual access/refresh tokens now
   * live in httpOnly cookies set by the backend — they are NEVER stored
   * or readable here. This cache is only for optimistic UI on load; the
   * real session check is GET /api/admin/session (see AdminLayout).
   * NOTE: cookies are shared across every browser tab (unlike sessionStorage,
   * which was tab-isolated) — logging in on one tab now logs in every tab.
   */
  setSession: (admin, role) => {
    sessionStorage.setItem('admin', JSON.stringify(admin));
    sessionStorage.setItem('adminRole', role);
  },

  /**
   * Get admin data
   */
  getAdmin: () => {
    const adminStr = sessionStorage.getItem('admin');
    return adminStr ? JSON.parse(adminStr) : null;
  },

  /**
   * Get admin role
   */
  getRole: () => {
    return sessionStorage.getItem('adminRole') || 'staff';
  },

  /**
   * Optimistic check based on the cached display object — NOT proof of a
   * valid session (the httpOnly cookie is the source of truth server-side).
   */
  isLoggedIn: () => {
    return !!sessionStorage.getItem('admin');
  },

  /**
   * Clear admin session only
   */
  clear: () => {
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('admin');
    sessionStorage.removeItem('adminRole');
    sessionStorage.removeItem('adminRefreshToken');
  }
};

// ==================== CUSTOMER STORAGE (localStorage - Persists across sessions) ====================

export const CustomerStorage = {
  /**
   * Cache the non-sensitive user/guest display object after login.
   * SECURITY (2026-09 migration): the actual access/refresh tokens now
   * live in httpOnly cookies set by the backend — they are NEVER stored
   * or readable here. This cache is only for optimistic UI on load; the
   * real session check is GET /api/auth/session (see AuthContext).
   */
  setSession: (user) => {
    localStorage.setItem('user', JSON.stringify(user));
  },

  /**
   * Get customer data
   */
  getUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  /**
   * Optimistic check based on the cached display object — NOT proof of a
   * valid session (the httpOnly cookie is the source of truth server-side).
   */
  isLoggedIn: () => {
    return !!(localStorage.getItem('user') || localStorage.getItem('guest'));
  },

  /**
   * Clear customer session. Wipes auth tokens + profile + the per-tab
   * WIP booking-flow snapshot. We no longer keep any cross-page booking
   * pre-fill in localStorage (removed 2026-05-17), so this is auth-only +
   * the sessionStorage WIP key.
   */
  clear: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('guest');
    // Belt & braces: clear any legacy keys that may exist from older builds.
    localStorage.removeItem('bookingIntent');
    localStorage.removeItem('bba_selection');
    localStorage.removeItem('hoopbook_selection');
    try {
      sessionStorage.removeItem('bba_flow_snapshot');
      sessionStorage.removeItem('bba_guest_intent');
    } catch (e) {
      console.warn('Could not clear flow snapshot from sessionStorage:', e);
    }
  },

  /**
   * Clear ONLY authentication-related keys. Used when flipping sessions
   * (e.g., guest → registered user) without wiping the in-tab WIP snapshot.
   */
  clearAuthOnly: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('guest');
  }
};

/**
 * True if a customer session (registered or guest) was already active on
 * this device BEFORE the current login/signup call writes its own token.
 * Callers use this to detect a same-tab, same-device session *switch*
 * (User A never explicitly logged out; User B is now authenticating) so
 * they can decide whether a leftover `GuestIntent` pick can be trusted to
 * belong to the incoming user.
 */
export const hadPriorCustomerSession = () =>
  !!(localStorage.getItem('user') || localStorage.getItem('guest'));

// ==================== GUEST BOOKING INTENT (sessionStorage) ====================
// Captures a pre-login slot selection from the landing page so a guest who
// has to log in first can resume straight into the booking flow instead of
// landing on /home. Tab-scoped (sessionStorage) + 30-min TTL — auto-clears on
// tab close, never persists across sessions (avoids the localStorage
// staleness bug from the earlier bookingIntent implementation, removed
// 2026-05-17 — see useBookingFlow.js comments).
const GUEST_INTENT_KEY = 'bba_guest_intent';
const GUEST_INTENT_MAX_AGE_MIN = 30;

export const GuestIntent = {
  set: (locationId, date, timeSlot) => {
    try {
      sessionStorage.setItem(GUEST_INTENT_KEY, JSON.stringify({
        locationId, date, timeSlot, savedAt: Date.now(),
      }));
    } catch (e) {
      console.warn('Could not save guest booking intent:', e?.message || e);
    }
  },

  /** Returns the intent if present and fresh, else null (clearing if stale). */
  get: () => {
    try {
      const raw = sessionStorage.getItem(GUEST_INTENT_KEY);
      if (!raw) return null;
      const intent = JSON.parse(raw);
      const ageMin = (Date.now() - (intent.savedAt || 0)) / 60000;
      if (ageMin >= GUEST_INTENT_MAX_AGE_MIN) {
        sessionStorage.removeItem(GUEST_INTENT_KEY);
        return null;
      }
      return intent;
    } catch (e) {
      sessionStorage.removeItem(GUEST_INTENT_KEY);
      return null;
    }
  },

  clear: () => {
    sessionStorage.removeItem(GUEST_INTENT_KEY);
  },
};

/**
 * Where to send the customer immediately after a successful login/signup —
 * straight into the booking flow if they had a pre-login slot selection,
 * otherwise the normal home page.
 */
export const getPostLoginRedirect = () => (GuestIntent.get() ? '/book' : '/home');

// ==================== GLOBAL STORAGE MANAGEMENT ====================

/**
 * Clear ALL storage (admin + customer)
 * CRITICAL: Use this when switching between admin and customer contexts
 */
export const clearAllStorage = () => {
  // Clear admin session
  AdminStorage.clear();
  
  // Clear customer session
  CustomerStorage.clear();
  
  // Clear any legacy keys
  localStorage.removeItem('adminToken');  // Old admin token in localStorage
  sessionStorage.removeItem('token');     // Old customer token in sessionStorage
  
  console.log('🧹 All storage cleared (admin + customer sessions)');
};

/**
 * Get authentication header for API calls
 * Context-aware: checks appropriate storage based on context
 */
export const getAuthHeader = () => ({});

/**
 * SECURITY: Detect and clean mixed sessions
 * If admin token exists when customer logs in (or vice versa), clear it
 */
export const ensureCleanSession = (context = 'customer') => {
  if (context === 'customer' && AdminStorage.isLoggedIn()) {
    console.warn('⚠️ Admin session detected during customer login - clearing admin session');
    AdminStorage.clear();
  } else if (context === 'admin' && CustomerStorage.isLoggedIn()) {
    console.warn('⚠️ Customer session detected during admin login - clearing customer session');
    CustomerStorage.clear();
  }
};

export default {
  AdminStorage,
  CustomerStorage,
  GuestIntent,
  hadPriorCustomerSession,
  getPostLoginRedirect,
  clearAllStorage,
  getAuthHeader,
  ensureCleanSession
};
